import {
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateCarWash } from "../../../../services/carWash.service";
import { ICarWashObject } from "../../../../types/carWash";
import { useCarWash } from "../../../../context/CarWashContext";
import LocationMapModal from "../../ManageCarWashes/components/LocationMapModal";

type Props = {};

const ContactDetails = (props: Props) => {
  const { t } = useTranslation();
  const { carWash, loading: carWashLoading, refetch } = useCarWash();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<ICarWashObject | null>(null);
  const [loading, setLoading] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  useEffect(() => {
    if (carWash) {
      setEditedData(carWash);
    }
  }, [carWash]);

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset to original data
      setEditedData(carWash);
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!editedData || !carWash?._id) return;

    setLoading(true);
    try {
      await updateCarWash(carWash._id, editedData);
      await refetch();
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: t("contactDetails.updatedSuccess"),
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || t("contactDetails.updateFailed"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOwnerChange = (field: string, value: string) => {
    setEditedData((prev: any) => ({
      ...prev,
      owner: {
        ...prev.owner,
        [field]: value,
      },
    }));
  };

  const handleContactChange = (field: string, value: string) => {
    setEditedData((prev: any) => ({
      ...prev,
      contact: {
        ...prev.contact,
        [field]: value,
      },
    }));
  };

  const handleLocationChange = (field: string, value: string) => {
    setEditedData((prev: any) => ({
      ...prev,
      location: {
        ...prev.location,
        [field]: value,
      },
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (carWashLoading || !carWash) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography>{t("contactDetails.loading")}</Typography>
      </Box>
    );
  }

  const carWashInfo = carWash;
  const hasCoords =
    !!carWashInfo?.location?.latitude && !!carWashInfo?.location?.longitude;

  return (
    <Box p={3} borderRadius={2} boxShadow={3} bgcolor="background.paper">
      {/* Header with Edit Toggle */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" gutterBottom>
          {t("carWashes.tabContactDetails")}
        </Typography>
        <Box>
          {isEditing ? (
            <>
              <IconButton
                onClick={handleSave}
                disabled={loading}
                color="primary"
                title={t("common.saveChanges")}
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                onClick={handleEditToggle}
                disabled={loading}
                color="secondary"
                title={t("common.cancelEditing")}
              >
                <CancelIcon />
              </IconButton>
            </>
          ) : (
            <IconButton
              onClick={handleEditToggle}
              color="primary"
              title={t("carWashes.editContact")}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Owner Information */}
        <Grid size={6}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PersonIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">{t("carWashes.ownerInformation")}</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={12}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label={t("common.firstName")}
                      value={editedData?.owner?.firstName || ""}
                      onChange={(e) =>
                        handleOwnerChange("firstName", e.target.value)
                      }
                      margin="dense"
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      <strong>{t("common.name")}:</strong> {carWashInfo?.owner.firstName}{" "}
                      {carWashInfo?.owner.lastName}
                    </Typography>
                  )}
                </Grid>

                <Grid size={12}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label={t("common.lastName")}
                      value={editedData?.owner?.lastName || ""}
                      onChange={(e) =>
                        handleOwnerChange("lastName", e.target.value)
                      }
                      margin="dense"
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      <strong>{t("common.phone")}:</strong> {carWashInfo?.owner.country_code}{" "}
                      {carWashInfo?.owner.phone}
                    </Typography>
                  )}
                </Grid>

                <Grid size={12}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label={t("common.phone")}
                      value={editedData?.owner?.phone || ""}
                      onChange={(e) =>
                        handleOwnerChange("phone", e.target.value)
                      }
                      margin="dense"
                      InputProps={{
                        startAdornment: (
                          <Typography variant="caption" sx={{ mr: 1 }}>
                            {editedData?.owner?.country_code || "+1"}
                          </Typography>
                        ),
                      }}
                    />
                  ) : null}
                </Grid>

                <Grid size={12}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label={t("common.countryCode")}
                      value={editedData?.owner?.country_code || ""}
                      onChange={(e) =>
                        handleOwnerChange("country_code", e.target.value)
                      }
                      margin="dense"
                    />
                  ) : null}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Information */}
        <Grid size={6} sx={{ display: "flex" }}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PhoneIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">{t("contactDetails.contactInformation")}</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={12}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label={t("common.fullName")}
                      value={editedData?.contact?.fullName || ""}
                      onChange={(e) =>
                        handleContactChange("fullName", e.target.value)
                      }
                      margin="dense"
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      <strong>{t("carWashDetails.contactPerson")}:</strong>{" "}
                      {carWashInfo?.contact.fullName}
                    </Typography>
                  )}
                </Grid>

                <Grid size={12}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label={t("common.phone")}
                      value={editedData?.contact?.phone || ""}
                      onChange={(e) =>
                        handleContactChange("phone", e.target.value)
                      }
                      margin="dense"
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      <strong>{t("carWashes.contactPhone")}:</strong>{" "}
                      {carWashInfo?.contact.phone}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Location Information */}
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Box display="flex" alignItems="center">
                  <LocationIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t("carWashes.locationInformation")}</Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LocationIcon />}
                  disabled={!hasCoords}
                  onClick={() => setMapOpen(true)}
                >
                  {t("common.viewOnMap")}
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={6}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label={t("common.street")}
                      value={editedData?.location?.street || ""}
                      onChange={(e) =>
                        handleLocationChange("street", e.target.value)
                      }
                      margin="dense"
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      <strong>{t("common.address")}</strong>{" "}
                      {carWashInfo?.location?.street || (carWashInfo?.location as any)?.streetName || t("common.notAvailable")}
                    </Typography>
                  )}
                </Grid>

                <Grid size={6}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label={t("common.description")}
                      value={editedData?.location?.description || ""}
                      onChange={(e) =>
                        handleLocationChange("description", e.target.value)
                      }
                      margin="dense"
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      <strong>{t("common.description")}:</strong>{" "}
                      {carWashInfo?.location.description ||
                        t("carWashes.noDescription")}
                    </Typography>
                  )}
                </Grid>

                <Grid size={6}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label={t("common.latitude")}
                      value={editedData?.location?.latitude || ""}
                      onChange={(e) =>
                        handleLocationChange("latitude", e.target.value)
                      }
                      margin="dense"
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      <strong>{t("common.latitude")}:</strong>{" "}
                      {carWashInfo?.location.latitude}
                    </Typography>
                  )}
                </Grid>

                <Grid size={6}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label={t("common.longitude")}
                      value={editedData?.location?.longitude || ""}
                      onChange={(e) =>
                        handleLocationChange("longitude", e.target.value)
                      }
                      margin="dense"
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      <strong>{t("common.longitude")}:</strong>{" "}
                      {carWashInfo?.location.longitude}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Business Information */}
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <EmailIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">{t("ownerInfo.businessInfo")}</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>{t("common.legalName")}:</strong> {carWashInfo?.legalName}
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>{t("carWashTable.rating")}:</strong> {carWashInfo?.rating || 0} / 5
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>{t("ownerInfo.created")}:</strong>{" "}
                    {new Date(
                      carWashInfo?.createdAt || ""
                    ).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <LocationMapModal
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        latitude={carWashInfo?.location?.latitude}
        longitude={carWashInfo?.location?.longitude}
        title={carWashInfo?.legalName}
        address={carWashInfo?.location?.street}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactDetails;
